import React, { useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollContext } from './ScrollContainer';

gsap.registerPlugin(ScrollTrigger);

const WhatIDoSVG = () => (
  <svg width="100%" height="100%" viewBox="0 0 5172 771" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1022.19 11L849.188 716H608.188L511.188 284L412.188 716H171.188L0.187501 11H212.188L294.188 493L405.188 11H620.188L728.188 489L810.188 11H1022.19ZM1716.36 11V716H1520.36V433H1281.36V716H1085.36V11H1281.36V276H1520.36V11H1716.36ZM2254.52 601H2004.52L1966.52 716H1760.52L2017.52 11H2243.52L2499.52 716H2292.52L2254.52 601ZM2205.52 451L2129.52 223L2054.52 451H2205.52ZM3074.41 11V167H2886.41V716H2690.41V167H2504.41V11H3074.41ZM3494.33 11V716H3298.33V11H3494.33ZM4036.35 11C4110.35 11 4175.02 26 4230.35 56C4286.35 85.3333 4329.35 126.667 4359.35 180C4389.35 233.333 4404.35 294.333 4404.35 363C4404.35 431 4389.02 491.667 4358.35 545C4328.35 598.333 4285.35 640.333 4229.35 671C4174.02 701 4109.68 716 4036.35 716H3757.35V11H4036.35ZM3953.35 167V560H4036.35C4097.02 560 4142.35 539.333 4172.35 498C4202.35 456.667 4217.35 400.333 4217.35 329C4217.35 277 4205.35 235 4181.35 203C4158.02 171 4118.02 155 4061.35 155C4052.02 155 4016.02 159 3953.35 167ZM4830.73 11C4938.06 11 5022.06 48 5082.73 122C5142.73 196 5172.73 286.333 5172.73 393C5172.73 496.333 5142.4 585.333 5081.73 660C5021.06 734 4937.4 771 4830.73 771C4724.06 771 4640.4 734 4579.73 660C4519.06 585.333 4488.73 496.333 4488.73 393C4488.73 286.333 4519.06 196 4579.73 122C4640.4 48 4724.06 11 4830.73 11ZM4830.73 162C4778.06 162 4737.73 186.667 4709.73 236C4681.73 285.333 4667.73 349.333 4667.73 428C4667.73 506 4681.73 569.667 4709.73 619C4737.73 668.333 4778.06 693 4830.73 693C4883.4 693 4923.73 668.333 4951.73 619C4979.73 569.667 4993.73 506 4993.73 428C4993.73 349.333 4979.73 285.333 4951.73 236C4923.73 186.667 4883.4 162 4830.73 162Z"
      fill="#ffffff"
    />
  </svg>
);

const AboutWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
  color: var(--color-portfolio-silver-light);
  background: transparent;
`;

/* Tall scroll spacer that gives the sticky child room to "pin" */
const StickyTrack = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
  height: ${() => `${window.innerHeight * 6.0}px`};
`;

const CombinedSection = styled.section`
  position: sticky;
  top: 0;
  width: 100%;
  max-width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: transparent;
  box-sizing: border-box;
  padding: 2rem;
`;

const MarqueeGroup = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 10;
  overflow: hidden;
`;

const ServicesHeader = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: clamp(70px, 14vh, 180px);
  display: flex;
  justify-content: center;
  align-items: center;
  will-change: transform, opacity;

  svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const RevealTextWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 5rem;
  z-index: 5;
  will-change: opacity, transform;
`;

const SectionTag = styled.span`
  font-family: var(--font-portfolio-mono);
  font-size: clamp(0.75rem, 1.1vw, 0.95rem);
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-portfolio-wine-light);
  margin-bottom: 1.5rem;
  align-self: center;
`;

const TextParagraph = styled.p`
  font-family: var(--font-portfolio-display);
  font-size: clamp(1.6rem, 3.8vw, 3.2rem);
  line-height: 1.35;
  font-weight: 700;
  text-align: center;
  max-width: 1100px;
  color: var(--color-portfolio-silver-light);
  margin: 0 auto;

  .word {
    display: inline-block;
    will-change: opacity, filter, transform;
  }
`;

const About = () => {
  const wrapperRef = useRef(null);
  const combinedRef = useRef(null);
  const stickyTrackRef = useRef(null);

  const { containerRef: scrollContainerRef } = useContext(ScrollContext);

  const bioText =
    "Final-year Computer Engineering student at Fr. CRCE Bandra with a strong foundation in software engineering and a track record of building production-ready applications across AI, Web3, and full-stack development.";

  const splitWords = bioText.split(/(\s+)/).map((word, idx) => {
    if (word.match(/^\s+$/)) return word;
    return (
      <span className="word" key={idx} style={{ opacity: 0.1, filter: 'blur(5px)' }}>
        {word}
      </span>
    );
  });

  useEffect(() => {
    const scrollerElem = scrollContainerRef?.current || window;
    const section = combinedRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const headers = section.querySelectorAll('.services-header');
      const textWrapper = section.querySelector('.reveal-text-wrapper');
      const words = section.querySelectorAll('.word');

      // Initial positions — opacity + transform only (no CSS filter = no compositor lag)
      gsap.set(headers[0], { x: '100%', opacity: 1, force3D: true });
      gsap.set(headers[1], { x: '-100%', opacity: 1, force3D: true });
      gsap.set(headers[2], { x: '100%', opacity: 1, force3D: true });
      gsap.set(textWrapper, { opacity: 0, y: 30, force3D: true });
      gsap.set(words, { opacity: 0.1, filter: 'blur(5px)', force3D: true });

      // Scrub timeline driven by the tall StickyTrack wrapper.
      // CSS position:sticky handles the pinning — zero DOM injection, zero reflow.
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: stickyTrackRef.current,
          scroller: scrollerElem,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.8,
        },
      });

      // 1. Horizontal slide-in (0 -> 0.28) — Slower, more gradual entrance
      tl.to(headers[0], { x: '0%', ease: 'power1.out', duration: 0.28 }, 0);
      tl.to(headers[1], { x: '0%', ease: 'power1.out', duration: 0.28 }, 0);
      tl.to(headers[2], { x: '0%', ease: 'power1.out', duration: 0.28 }, 0);

      // 2. Vertical stack (0.28 -> 0.48) — Smooth, relaxed merging
      tl.to(headers[0], { y: '100%', ease: 'power1.inOut', duration: 0.20 }, 0.28);
      tl.to(headers[2], { y: '-100%', ease: 'power1.inOut', duration: 0.20 }, 0.28);

      // 3. Rapid dissolve of outer rows as they merge (0.44 -> 0.52)
      tl.to([headers[0], headers[2]], { opacity: 0, ease: 'power2.out', duration: 0.08 }, 0.44);

      // 4. Move single header (row 1) up to top center & scale down (0.48 -> 0.70)
      tl.to(
        headers[1],
        {
          scale: 0.2,
          y: '-40vh',
          opacity: 1,
          ease: 'power1.out',
          duration: 0.22,
        },
        0.48
      );

      // 5. Fade in bio text directly underneath (0.68 -> 0.76)
      tl.to(textWrapper, { opacity: 1, y: 0, ease: 'power1.out', duration: 0.08 }, 0.68);

      // 6. Stagger word unblur & opacity reveal (0.76 -> 1.0)
      tl.to(
        words,
        {
          opacity: 1,
          filter: 'blur(0px)',
          stagger: 0.025,
          ease: 'none',
          duration: 0.24,
        },
        0.76
      );
    }, combinedRef);

    return () => ctx.revert();
  }, [scrollContainerRef]);

  return (
    <AboutWrapper id="about" ref={wrapperRef}>
      {/* Sticky-pinned section: tall track + sticky child = native pin, zero DOM injection */}
      <StickyTrack ref={stickyTrackRef}>
        <CombinedSection ref={combinedRef}>
          <MarqueeGroup className="marquee-group">
            <ServicesHeader className="services-header">
              <WhatIDoSVG />
            </ServicesHeader>
            <ServicesHeader className="services-header">
              <WhatIDoSVG />
            </ServicesHeader>
            <ServicesHeader className="services-header">
              <WhatIDoSVG />
            </ServicesHeader>
          </MarqueeGroup>

          <RevealTextWrapper className="reveal-text-wrapper">
            <SectionTag>01 // Background</SectionTag>
            <TextParagraph>{splitWords}</TextParagraph>
          </RevealTextWrapper>
        </CombinedSection>
      </StickyTrack>
    </AboutWrapper>
  );
};

export default About;
